import { useCallback, useEffect, type RefObject } from "react";

function isFormField(
  el: EventTarget | Element | null
): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}

/**
 * iOS Safari: nested overflow scroll + fixed shell prevents the browser from
 * keeping focused inputs visible when the keyboard opens. Re-scroll on focus
 * and after visualViewport resize.
 */
export function useWizardInputScrollIntoView(
  containerRef: RefObject<HTMLElement | null>,
  enabled = true
) {
  const scrollFieldIntoView = useCallback((el: HTMLElement) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void el.offsetHeight;
        el.scrollIntoView({ block: "nearest", inline: "nearest" });
      });
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      if (isFormField(e.target) && container.contains(e.target)) {
        scrollFieldIntoView(e.target);
      }
    };

    const handleViewportChange = () => {
      const active = document.activeElement;
      if (isFormField(active) && container.contains(active)) {
        scrollFieldIntoView(active);
      }
    };

    container.addEventListener("focusin", handleFocusIn);
    const vv = window.visualViewport;
    vv?.addEventListener("resize", handleViewportChange);
    vv?.addEventListener("scroll", handleViewportChange);

    return () => {
      container.removeEventListener("focusin", handleFocusIn);
      vv?.removeEventListener("resize", handleViewportChange);
      vv?.removeEventListener("scroll", handleViewportChange);
    };
  }, [enabled, containerRef, scrollFieldIntoView]);
}
